import { useEffect, useMemo, useRef } from 'react';
import {
  StationeryTemplateCategoryEnum,
  useCardDashboardStationeryTemplatesContainerQuery,
  usePrintDesignsGalleryEventInfoByEventHandleQuery,
  useStationeryProductsQuery,
} from '@graphql/generated';

import { useManagedSearchParam } from '@apps/admin/common/hooks/useManagedSearchParam';
import { useCardCategoryContext } from '../CardCategoryProvider';
import { useFeatureValue } from '@shared/core/featureFlags';
import { ThemeJson } from '@apps/card/routes/CardCustomizer';
import { StationeryTemplate } from './DesignsGallery.types';
import { processInitialQueryData } from './utils';
import { useApplyFiltersToTemplates } from './useApplyFiltersToTemplates';
import { useDesignsGalleryHealth } from './DesignsGallery.health';
import { injectProducts } from './injectProducts';
import { FacetOptionLocal } from './components/Filters/Filter.types';
import { isDigitalProduct } from '@apps/card/components/ProductUIConfig/isDigitalProduct';
import { useFilterFacets } from './useFilterFacets';

type UseDesignTemplatesArgs = {
  /**
   * Marking as optional to prepare for the future when card app is no longer tied to the admin view
   */
  health?: ReturnType<typeof useDesignsGalleryHealth>;
  eventId?: string;
  eventHandle?: string;
  categories?: StationeryTemplateCategoryEnum[];
  themeIds?: string[];
  skipDigitalDesigns?: boolean;
};

export type StationeryTemplateWithThemeJson = StationeryTemplate & { themeJSON: ThemeJson };

const themeIdWithOrientation = (template: StationeryTemplate) => {
  if (template.widthInches > template.heightInches) {
    return `wide ${template.themeId}`;
  }
  if (template.heightInches > template.widthInches) {
    return `tall ${template.themeId}`;
  }
  return template.themeId;
};

/*
 * This method allows setting order of top N items in the gallery based on array of themeIds coming from Amplitude Feature flag
 */
const setGalleryOrder = (templates: StationeryTemplate[], themeOrder: string[]): StationeryTemplate[] => {
  if (templates?.length < 1 || themeOrder?.length < 1) return templates;
  try {
    const themeLookup = new Map(themeOrder.map((themeId, index) => [themeId, index]));
    return [...templates].sort((a, b) => {
      const aIndex = themeLookup.get(themeIdWithOrientation(a)) ?? themeLookup.get(a?.themeId) ?? Number.POSITIVE_INFINITY;
      const bIndex = themeLookup.get(themeIdWithOrientation(b)) ?? themeLookup.get(b?.themeId) ?? Number.POSITIVE_INFINITY;
      return aIndex - bIndex;
    });
  } catch {
    console.error('Error sorting templates by printGalleryThemeDisplayOrder.  Using default sort order');
    return templates;
  }
};

export const useDesignTemplates = (args: UseDesignTemplatesArgs) => {
  const { eventId, eventHandle, categories, themeIds, skipDigitalDesigns, health } = args;
  const { currentCategory } = useCardCategoryContext();
  const [themeQueryParam] = useManagedSearchParam('theme');
  const themeQueryParamRef = useRef(themeQueryParam);
  const { value, payload } = useFeatureValue('printGalleryThemeDisplayOrder');

  const { applyFiltersToTemplates } = useApplyFiltersToTemplates();

  const { data: eventInfoData } = usePrintDesignsGalleryEventInfoByEventHandleQuery({
    batchMode: 'fast',
    variables: {
      eventHandle: eventHandle || '',
    },
    fetchPolicy: 'cache-first',
    skip: !eventHandle,
  });

  const shouldSkipFetchingDigitalDesignData = !(currentCategory === StationeryTemplateCategoryEnum.invitation || currentCategory === StationeryTemplateCategoryEnum.saveTheDate);

  const eventDesignThemeId = eventInfoData?.eventByName?.eventDesign?.theme.themeId;

  const { data: templateData, error } = useCardDashboardStationeryTemplatesContainerQuery({
    variables: {
      filter: {
        categories: categories ?? [currentCategory],
        side: 'front',
        themeIds,
      },
      // Requesting a number larger than what we have in the DB while we wait on
      // designs for pagination
      first: 20000,
      skipDigitalDesigns: skipDigitalDesigns || shouldSkipFetchingDigitalDesignData,
      includeOptions: {
        includeFavoritesByEventIds: eventId ? [eventId] : undefined,
        includeProducts: true,
      },
    },
    batchMode: 'fast',
    fetchPolicy: 'cache-first',
    ssr: false,
  });

  const { data: productData, error: productsError } = useStationeryProductsQuery({
    batchMode: 'fast',
    fetchPolicy: 'cache-first',
    ssr: false,
  });

  useEffect(() => {
    if (error || productsError) {
      health?.onLoadFailure(error ?? productsError);
    } else if (templateData?.stationeryTemplatesContainer) {
      health?.onLoadComplete();
    }
  }, [error, templateData, health, productsError]);

  if (error) throw error;
  if (productsError) throw productsError;

  const products = useMemo(() => {
    return productData?.stationeryProducts ?? [];
  }, [productData]);

  const templates = useMemo(() => {
    const mappedTemplates = injectProducts(processInitialQueryData(templateData, currentCategory), productData);

    // Conceptually part of `processedInitialPrintableTemplates`. However this part can only be done after
    // injectProducts. TODO: Consider all `mappedTags`logic to post-injectProducts.
    for (const template of mappedTemplates) {
      const templateFormats = new Set<FacetOptionLocal>(template.products.map(p => (isDigitalProduct(p) ? 'format:digital' : 'format:paper')));
      template.mappedTags.push(...Array.from(templateFormats));
    }

    let sortedTemplates: StationeryTemplate[] = [...mappedTemplates];

    if (value === 'on') {
      if (Array.isArray(payload)) {
        sortedTemplates = setGalleryOrder(mappedTemplates, payload);
      } else if (payload && typeof payload === 'object') {
        // Expected format:
        // {
        //   "saveTheDate": [ ... ],
        //   "invitation": [ ... ],
        //   "enclosure": [
        //     "wide modern_monogram_navy",
        //     "tall upload_your_design",
        //     "weekend_events_blush",
        //     "tall modern_monogram_navy"
        //   ],
        //   "thankYou": [ ... ],
        //   "holiday": [ ... ]
        // }

        const themeIdListByCategory = payload as Record<string, string[]>;
        const themeIdList = themeIdListByCategory[currentCategory];
        if (Array.isArray(themeIdList)) {
          sortedTemplates = setGalleryOrder(mappedTemplates, themeIdList);
        }
      }
    }

    if (themeQueryParamRef.current || eventDesignThemeId) {
      // Sorting in place since we're already creating a new array above when we map the templates

      sortedTemplates.sort((a, b) => {
        // Prioritize the theme query param
        if (a.themeId === themeQueryParamRef.current) {
          return -1;
        } else if (b.themeId === themeQueryParamRef.current) {
          return 1;
        }

        // If no theme query param, prioritize the event design theme
        if (a.themeId === eventDesignThemeId) {
          return -1;
        } else if (b.themeId === eventDesignThemeId) {
          return 1;
        }

        return 0;
      });
    }

    return sortedTemplates;
  }, [templateData, currentCategory, productData, value, eventDesignThemeId, payload]);

  const filterFacets = useFilterFacets(templates);

  return {
    templates: applyFiltersToTemplates(templates),
    products,
    filterFacets,
    eventDesignThemeId,
    currentCategory,
  };
};
